import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { OWObjectList } from "OW.Remoting/Remoting/DataTypes/OWObjectList";
import { ErrorHandler } from "UI/Errors";
import { HTTPRequest } from "../../../Global/Base/Comunication/HTTPRequest";
import { HTTPStatus } from "../../../Global/Base/Comunication/HTTPStatus";
import { InvalidArgumentException } from "../../../Global/Base/Exceptions";
import { GlobalContext } from "../../../Global/Base/GlobalContext";
import { AsyncLock, Releaser } from "../../../Global/Base/Promises/AsyncLock";
import { CancellationToken } from "../../../Global/Base/Promises/CancellationTokenSource";
import { PromiseUtils } from "../../../Global/Base/Promises/PromiseUtils";
import { DateUtils } from "../../../Global/Base/Utils/DateUtils";
import { OWManager } from "../../../OW.Remoting/Remoting/OWManager";
import { RequestContext } from "../../../OW.Remoting/Remoting/RequestContext";
import { LocalizedStringBuilder } from "./DataBuilders/LocalizedStringBuilder";
import { MessageButtonType, MessageWindow } from "./MessageWindow";
import { UIManager } from "./UIManager";

const logger = LazyLoadingSectionLogger.create(LoggingSection.download);

/**
     * Třída umožňuje stahování dokumentů se zpracováním chybových stavů
     * @includeToDoc
     */
export class DownloadManager {
    private static _backoffInterval = 3000;
    private static _downloadPreflightLock: AsyncLock = new AsyncLock();

    /**
     * Volitelná funkce umožňující vygenerovat vlastní název pro balík souborů ke stažení
     * @param className Název třídy, ze které se budou soubory stahovat
     * @param ids Id záznamů, ze kterých se mají stahovat dokumenty
     * @param attrNames Názvy atributů třídy, ze kterých se mají stahovat dokumenty
     */
    public static archiveNameGeneratorFunction?: (className: string, ids: number[], attrNames: string[]) => Promise<string>;

    private static _defaultArchiveNameGeneratorFunction(): string {
        return `Archive-${DateUtils.dateToString(new Date(), "yyyy-MM-dd--HH-mm-ss")}`;
    }

    /**
     * Spustí přípravu stahování souboru a zajistí, že po jejím doběhnutí je už nachystaný nebo došlo ke zrušení stahování.
     * V chybových situacích informuje uživatele oknem s chybovou hláškou nebo potvrzovacím dialogem.
     * @param url Url ke stažení dokumentu
     * @param autoRetry Zda se nemá zobrazovat potvrzovací dialog na "stažení až bude soubor připravený"
     * @param downloadPreparedCallback Volitelný callback vyvolaný v okamžiku doběhnutí přípravy (a potřebných kontrol)
     * @return True pokud je soubor připravený ke stahování, false když došlo ke zrušení uživatelem nebo k neočekávané chybě
     */
    public static async prepareDownload(url: string, autoRetry: boolean = false, downloadPreparedCallback?: (success: boolean) => void): Promise<boolean> {
        let lock: Releaser;
        try {
            UIManager.instance.showLoader();

            lock = await GlobalContext.getType<typeof DownloadManager>(DownloadManager)._downloadPreflightLock.lock();

            let autoRetryConfirmed = false;

            for (;;) {
                // nejdříve se udělá preflight HEAD dotaz na stav přípravy stahování
                const response = await HTTPRequest.universalRequest({
                    requestMethod: "HEAD",
                    url: url,
                    readResponseInfos: true,
                    okStatusSet: [HTTPStatus.ok, HTTPStatus.accepted, HTTPStatus.tooManyRequests],
                }, CancellationToken.none);

                if (response.status === HTTPStatus.tooManyRequests) {
                    // Překročen počet souběžných stahování
                    // Počkejte na dokončení probíhajícího stahování a akci opakujte.
                    MessageWindow.showAlert(new LocalizedStringBuilder("GUI-641123"), "GUI-641122");
                    return DownloadManager._returnWithCallback(false, downloadPreparedCallback);
                }

                if (response.status === HTTPStatus.accepted) {
                    if (!autoRetryConfirmed) {
                        if (!autoRetry) {
                            logger.log("dialog shown");
                            // Váš soubor se připravuje
                            // Stahování se zahájí automaticky. Mezitím můžete pokračovat v práci.
                            const dialogResult = await MessageWindow.showYesNoDialog(new LocalizedStringBuilder("GUI-641125"),
                                "GUI-666839", // Stáhnout
                                "GUI-33412", // Zrušit
                                "GUI-641124");

                            if (dialogResult.result !== MessageButtonType.ok)
                                return DownloadManager._returnWithCallback(false, downloadPreparedCallback);

                            if (downloadPreparedCallback) {
                                downloadPreparedCallback(true);
                                downloadPreparedCallback = undefined;
                            }
                        }

                        lock.release();
                        UIManager.instance.hideLoader();
                        autoRetryConfirmed = true;
                    } else {
                        await PromiseUtils.delay(DownloadManager._backoffInterval);
                    }
                    continue;
                }

                return DownloadManager._returnWithCallback(true, downloadPreparedCallback);
            }
        } catch (e) {
            ErrorHandler.instance.handleError(e);
            return DownloadManager._returnWithCallback(false, downloadPreparedCallback);
        } finally {
            UIManager.instance.hideLoader();
            if (lock)
                lock.release();
        }
    }

    /**
     * Vrátí hodnotu success a vyvolá callback, pokud je specifikovaný
     */
    private static _returnWithCallback(success: boolean, callback?: (success: boolean) => void): boolean {
        if (callback)
            callback(success);
        return success;
    }

    /**
     * Stáhne požadovaný dokument
     * @param instanceID id instance objektu, ve kterém je dokument
     * @param attrName název atributu objektu, ve kterém je dokument
     * @param versionId ID verze dokumentu
     * @param downloadPreparedCallback Volitelný callback vyvolaný v okamžiku doběhnutí přípravy (a potřebných kontrol)
     * @param requestContext Viz {@link RequestContext}
     */
    public static async downloadFile(instanceID: string, attrName: string, versionId: string = "", downloadPreparedCallback?: () => void, requestContext?: RequestContext): Promise<void> {
        if (!instanceID || !attrName)
            throw new InvalidArgumentException();

        await DownloadManager._download(() => OWManager.getDocumentDownloadUrl(instanceID, attrName, versionId, requestContext), downloadPreparedCallback);
    }

    /**
     * Stáhne soubory z DMS v rámci jednoho archivu
     * @param objList ObjectList se záznamy, ze kterých se mají stahovat dokumenty
     * @param attrNames Názvy atributů třídy, ze kterých se mají stahovat dokumenty
     * @param downloadPreparedCallback Volitelný callback vyvolaný v okamžiku doběhnutí přípravy (a potřebných kontrol)
     * @param requestContext Viz {@link RequestContext}
     */
    public static async downloadFiles(objList: OWObjectList, attrNames: string[], downloadPreparedCallback?: () => void, requestContext?: RequestContext): Promise<void> {
        if (!objList || !attrNames || attrNames.length === 0)
            throw new InvalidArgumentException();

        try {
            const fileName = DownloadManager.archiveNameGeneratorFunction ? await DownloadManager.archiveNameGeneratorFunction(objList.className, objList.items.map(i => parseInt(i.id)), attrNames) : DownloadManager._defaultArchiveNameGeneratorFunction();
            await DownloadManager._download(() => OWManager.getDocumentsDownloadUrl(fileName, objList, attrNames, requestContext), downloadPreparedCallback);
        } catch (e) {
            DownloadManager._returnWithCallback(false, downloadPreparedCallback);
            throw e;
        }
    }

    private static async _download(urlProvider: () => Promise<string>, downloadPreparedCallback?: () => void): Promise<void> {
        try {
            const url = await urlProvider();

            if (!(await DownloadManager.prepareDownload(url, undefined, downloadPreparedCallback)))
                return;

            const a = document.createElement("a");
            a.href = url;
            a.download = "download";
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (ex) {
            ErrorHandler.instance.handleError(ex);
            DownloadManager._returnWithCallback(false, downloadPreparedCallback);
        }
    }
}

GlobalContext.registerType(DownloadManager);
